import React, { ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import classNames from 'classnames/bind';
import { Loader } from '@creditsights/styleguide-web';
import { useNavigate } from 'react-router-dom';
import { handleRedirectUrlClick, isInternalRedirect, isReplaceable } from '../../utils/articleUtils';
import styles from './PdfViewer.module.scss';
import 'pdfjs-dist/build/pdf.worker.entry';

const cx = classNames.bind(styles);

export interface PdfViewerProps {
  file: string;
}
export const MAX_PDF_WIDTH = 1300;
const PdfViewer = ({ file }: PdfViewerProps): ReactElement | null => {
  const [numPages, setNumPages] = useState(null);
  const [pdfWidth, setPdfWidth] = useState(0);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();

  const ref = useRef<Document>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = containerRef?.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver(() => {
      setPdfWidth(Math.min(element.offsetWidth ?? 0, MAX_PDF_WIDTH));
    });

    observer.observe(element);
    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    setPdfWidth(Math.min(containerRef.current?.offsetWidth ?? 0, MAX_PDF_WIDTH));
  }, []);

  useEffect(() => {
    if (ref.current && !(ref.current as any).linkService.current.addLinkAttributesOrg) {
      (ref.current as any).linkService.current.addLinkAttributesOrg = (
        ref.current as any
      ).linkService.current.addLinkAttributes;

      (ref.current as any).linkService.current.addLinkAttributes = (
        link: HTMLAnchorElement,
        url: string,
        newWindow: boolean
      ) => {
        if (isReplaceable(url) || isInternalRedirect(url)) {
          /* eslint-disable no-param-reassign */
          link.href = '#';
          link.target = '_blank';
          link.onclick = (event) => {
            event.preventDefault();
            const urlObject = new URL(url);
            const redirectPath = url.includes('/redirect')
              ? urlObject.pathname + urlObject.search
              : `/redirect?url=${url}`;
            handleRedirectUrlClick(event.ctrlKey, redirectPath, navigate);
          };
          link.oncontextmenu = (event) => {
            event.preventDefault();
          };
          link.onauxclick = (event) => {
            event.preventDefault();
          };
          /* eslint-enable no-param-reassign */
        } else {
          (ref.current as any).linkService.current.addLinkAttributesOrg(link, url, newWindow);
        }
      };
      setReady(true);
    }
  }, [ref.current]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const onDocumentLoading = () => {
    return (
      <div className={cx('loader')}>
        <Loader position="center" />
      </div>
    );
  };

  return (
    <div className={cx('body')} ref={containerRef}>
      <Document
        key="pdf-doc"
        data-testid="pdf-viewer"
        file={file}
        loading={onDocumentLoading}
        onLoadSuccess={onDocumentLoadSuccess}
        externalLinkTarget="_blank"
        ref={ref}
      >
        {ready &&
          Array.from(new Array(numPages), (el, index) => {
            return <Page key={`page_${index + 1}`} width={pdfWidth} pageNumber={index + 1} />;
          })}
      </Document>
    </div>
  );
};

export default PdfViewer;
